// Player profile server functions.
//   • listPlayerProfiles / getPlayerProfile are public: they feed the crawlable
//     /players directory and each /players/<handle> page during SSR and return
//     no account identifiers.
//   • saveMyProfile / deleteMyProfile are authenticated and write through the
//     caller's own client, so RLS plus the player_profiles trigger decide
//     ownership — a client can never write someone else's profile.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  handleSchema,
  profileInputSchema,
  type PlayerProfile,
  type Playstyle,
} from "./player-profiles-shared";

/** Every published profile for the public directory. */
export const listPlayerProfiles = createServerFn({ method: "GET" }).handler(
  async (): Promise<PlayerProfile[]> => {
    try {
      const { readProfileDirectory } = await import("./player-profiles.server");
      return await readProfileDirectory();
    } catch (e) {
      console.error("listPlayerProfiles failed", e);
      return [];
    }
  },
);

/** One published profile by handle. */
export const getPlayerProfile = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => handleSchema.parse(input))
  .handler(async ({ data }): Promise<PlayerProfile | null> => {
    try {
      const { readProfile } = await import("./player-profiles.server");
      return await readProfile(data.handle);
    } catch (e) {
      console.error("getPlayerProfile failed", e);
      return null;
    }
  });

export interface MyProfile {
  handle: string;
  displayName: string;
  bio: string;
  region: string;
  playstyle: Playstyle;
  favoritePal: string;
  favoriteCard: string;
  tradeReady: boolean;
  published: boolean;
}

/** The signed-in member's own profile, published or not. */
export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyProfile | null> => {
    const { data, error } = await context.supabase
      .from("player_profiles")
      .select("handle, display_name, bio, region, playstyle, favorite_pal, favorite_card, trade_ready, published")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    const row = data as unknown as {
      handle: string;
      display_name: string;
      bio: string;
      region: string;
      playstyle: Playstyle;
      favorite_pal: string;
      favorite_card: string;
      trade_ready: boolean;
      published: boolean;
    };
    return {
      handle: row.handle,
      displayName: row.display_name,
      bio: row.bio,
      region: row.region,
      playstyle: row.playstyle ?? "collector",
      favoritePal: row.favorite_pal,
      favoriteCard: row.favorite_card,
      tradeReady: row.trade_ready,
      published: row.published,
    };
  });

/** Create or update the signed-in member's profile. */
export const saveMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => profileInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    // A favourite card is optional, but when given it must be a real printing so
    // the profile never links to a dead card page.
    let favoriteCard = "";
    if (data.favoriteCard) {
      const { resolveOfficialCardCode } = await import("./tcg-cards");
      const code = resolveOfficialCardCode(data.favoriteCard);
      if (!code) {
        throw new Error(
          `"${data.favoriteCard}" is not an official Palworld TCG printing. Pick a card code such as EBP01-001.`,
        );
      }
      favoriteCard = code;
    }

    const { error } = await context.supabase.from("player_profiles").upsert(
      {
        user_id: context.userId,
        handle: data.handle,
        display_name: data.displayName,
        bio: data.bio,
        region: data.region,
        playstyle: data.playstyle,
        favorite_pal: data.favoritePal,
        favorite_card: favoriteCard,
        trade_ready: data.tradeReady,
        published: data.published,
      },
      { onConflict: "user_id" },
    );
    if (error) {
      if (error.code === "23505" || /duplicate key/i.test(error.message)) {
        throw new Error(`The profile URL /players/${data.handle} is already taken. Try another.`);
      }
      throw new Error(error.message);
    }
    return { ok: true as const, handle: data.handle };
  });

/** Remove the signed-in member's profile entirely. */
export const deleteMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { error } = await context.supabase
      .from("player_profiles")
      .delete()
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
